import type {
  CompositionV3DataQuery,
  GetPayV2Query,
} from '@revelio/data-access';

import { isEmptyCompositionBucket } from '../composition/composition-v3/is-empty-composition-bucket';
import {
  type BreakdownSingleDataSet,
  DataType,
  type SingleDataPoint,
} from '../types';

type PayV2 = NonNullable<GetPayV2Query['pay_v2']>;
type CompositionV3 = NonNullable<CompositionV3DataQuery['composition_v3']>;
type PayEntity = PayV2['entities'][number];

function getMostRecentMeanRate(
  payEntity: PayEntity,
  metric: 'total_spend_salary' | 'total_spend_compensation',
  exchangeRate: number
): number {
  const overtime = payEntity.metrics?.overtime;
  if (!overtime) return 0;
  const arr =
    metric === 'total_spend_salary'
      ? overtime.SalaryBase
      : overtime.SalaryTotal;
  if (!Array.isArray(arr) || arr.length === 0) return 0;
  const last = arr[arr.length - 1];
  return typeof last === 'number' ? last * exchangeRate : 0;
}

export function convertTotalSpendData({
  payData,
  compositionData,
  metric,
  secondaryDimension,
  exchangeRate = 1,
}: {
  payData: PayV2;
  compositionData: CompositionV3;
  metric: 'total_spend_salary' | 'total_spend_compensation';
  secondaryDimension: 'geography' | 'role';
  exchangeRate?: number;
}): BreakdownSingleDataSet {
  const payEntities = payData?.entities ?? [];
  const compositionEntities = compositionData?.entities ?? [];

  if (payEntities.length === 0 || compositionEntities.length === 0) {
    return { type: DataType.BREAKDOWN_SINGLE, data: [] };
  }

  const payBySegmentId = new Map<number, PayEntity>();
  for (const payEntity of payEntities) {
    const id = payEntity.metadata?.id;
    if (id !== undefined) payBySegmentId.set(id, payEntity);
  }

  const result: BreakdownSingleDataSet['data'] = compositionEntities.map(
    (compEntity) => {
      const primaryDimLabel =
        compEntity.short_name ?? compEntity.long_name ?? '';
      const compSegmentData = compEntity.metrics?.[secondaryDimension] ?? [];
      const breakdown: SingleDataPoint[] = [];

      for (const compSegment of compSegmentData) {
        const segmentId = compSegment.id;
        if (segmentId === undefined || isEmptyCompositionBucket(segmentId)) {
          continue;
        }
        const headcountSeries = compSegment.headcount;
        const lastHeadcount = headcountSeries?.[headcountSeries.length - 1];
        const headcount = lastHeadcount?.count ?? 0;

        const payEntity = payBySegmentId.get(segmentId);
        if (!payEntity) continue;
        const rate = getMostRecentMeanRate(payEntity, metric, exchangeRate);

        const value = rate * headcount;
        const segmentLabel =
          compSegment.short_name ?? compSegment.long_name ?? '';

        breakdown.push({ dim: segmentLabel, value, share: value });
      }

      return { dim: primaryDimLabel, breakdown };
    }
  );

  return { type: DataType.BREAKDOWN_SINGLE, data: result };
}
